import * as React from 'react';
import { Spin, Switch, Tooltip, message } from 'antd';
import { EyeOutlined, LoadingOutlined, RedoOutlined, SyncOutlined } from '@ant-design/icons';
import moment from 'jalali-moment';
import DatePicker from 'react-multi-date-picker';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';
import { TableColumn } from 'react-data-table-component';

import { Badge, BadgeType } from 'components/badge/badge';
import { Button, buttonType } from 'components/button/button';
import { Input } from 'components/input/input';
import { Modal } from 'components/modal/modal';
import { Table } from 'components/table/table';
import { ReactComponent as CalendarIcon } from 'assets/icons/dash-line-icons/calendar.svg';
import { BulkSmsItemStatus, BulkSmsPart, BulkSmsPartDetail, BulkSmsPartItem, PaginationList } from 'core/domain';
import {
    getBulkSmsPartItems,
    getBulkSmsParts,
    refreshBulkSmsPartStatus,
    retryBulkSmsItems,
} from 'core/repositories/bulk-sms';
import { jFormat, jFormatEndOfDay } from 'core/utils/jFormat';

const { useEffect, useState } = React;

interface Filters {
    keyword: string;
    from: string;
    to: string;
    onlyWithFailed: boolean;
}

const emptyFilters: Filters = { keyword: '', from: '', to: '', onlyWithFailed: false };

const statusBadges: Record<BulkSmsItemStatus, { text: string; type: BadgeType }> = {
    [BulkSmsItemStatus.Sent]: { text: 'ارسال‌شده', type: 'waiting' },
    [BulkSmsItemStatus.Delivered]: { text: 'رسیده', type: 'success' },
    [BulkSmsItemStatus.Failed]: { text: 'ناموفق', type: 'error' },
    [BulkSmsItemStatus.Undelivered]: { text: 'تحویل‌نشده', type: 'error' },
};

const formatDate = (date?: string) => (date ? moment.utc(date).locale('fa').local().format('YYYY/MM/DD HH:mm') : '-');

const buildQuery = (filters: Filters) => {
    const params: string[] = [];
    if (filters.keyword.trim()) params.push(`keyword=${encodeURIComponent(filters.keyword.trim())}`);
    if (filters.from) params.push(`startedDate=${jFormat(filters.from)}`);
    if (filters.to) params.push(`endDate=${jFormatEndOfDay(filters.to)}`);
    if (filters.onlyWithFailed) params.push('onlyWithFailed=true');
    return params.length ? `?${params.join('&')}` : '';
};

interface Props {
    /** شناسه‌ی ارسالی که از تب «ارسال جدید» برای نمایش انتخاب شده. */
    batchId?: string;
}

export const History: React.FC<Props> = ({ batchId }) => {
    const [filters, setFilters] = useState<Filters>({ ...emptyFilters, keyword: batchId ?? '' });
    const [query, setQuery] = useState('');
    const [parts, setParts] = useState<PaginationList<BulkSmsPart>>();
    const [loading, setLoading] = useState(false);
    const [pageSize, setPageSize] = useState(10);
    /** پارتی که عملیات ردیفش (استعلام / ارسال مجدد) در حال انجام است. */
    const [busyPartId, setBusyPartId] = useState<string>();

    // Detail modal
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailPartId, setDetailPartId] = useState<string>();
    const [detail, setDetail] = useState<BulkSmsPartDetail>();
    const [detailLoading, setDetailLoading] = useState(false);
    const [onlyFailed, setOnlyFailed] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    const load = (nextFilters: Filters, page = 1) => {
        const nextQuery = buildQuery(nextFilters);
        setQuery(nextQuery);
        getBulkSmsParts(setLoading, setParts, page, pageSize, nextQuery);
    };

    const reloadCurrentPage = () =>
        getBulkSmsParts(setLoading, setParts, parts?.pagination?.pageIndex || 1, pageSize, query);

    useEffect(() => {
        const next = { ...emptyFilters, keyword: batchId ?? '' };
        setFilters(next);
        load(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [batchId]);

    const loadDetail = (partId: string, failedOnly: boolean) =>
        getBulkSmsPartItems(setDetailLoading, setDetail, partId, failedOnly);

    const openDetail = (part: BulkSmsPart) => {
        setDetail(undefined);
        setOnlyFailed(false);
        setDetailPartId(part.id);
        setDetailOpen(true);
        loadDetail(part.id, false);
    };

    const refreshStatus = async (partId: string, setBusy: (loading: boolean) => void) => {
        const result = await refreshBulkSmsPartStatus(setBusy as any, partId);
        if (!result) return false;
        message.success(
            result.checkedCount
                ? `${result.checkedCount.toLocaleString('fa-IR')} پیامک استعلام شد.`
                : 'پیامکی در انتظار تحویل نبود.',
        );
        return true;
    };

    const retry = async (partId: string, setBusy: (loading: boolean) => void, itemIds?: string[]) => {
        const result = await retryBulkSmsItems(setBusy as any, partId, itemIds);
        if (!result) return false;
        const text = `${result.sentCount.toLocaleString('fa-IR')} ارسال شد، ${result.failedCount.toLocaleString('fa-IR')} ناموفق ماند.`;
        result.failedCount ? message.warning(text) : message.success(text);
        return true;
    };

    const setRowBusy = (partId: string) => (busy: boolean) => setBusyPartId(busy ? partId : undefined);

    const onRowRefresh = async (part: BulkSmsPart) => {
        if (await refreshStatus(part.id, setRowBusy(part.id))) reloadCurrentPage();
    };

    const onRowRetry = async (part: BulkSmsPart) => {
        if (await retry(part.id, setRowBusy(part.id))) reloadCurrentPage();
    };

    const afterDetailAction = (done: boolean) => {
        if (!done || !detailPartId) return;
        loadDetail(detailPartId, onlyFailed);
        reloadCurrentPage();
    };

    const columns: TableColumn<BulkSmsPart>[] = [
        {
            name: 'تاریخ',
            id: 'createdOn',
            selector: (row) => row.createdOn ?? '',
            cell: (row) => <span>{formatDate(row.createdOn)}</span>,
            minWidth: '130px',
        },
        {
            name: 'ارسال‌کننده',
            id: 'createdBy',
            selector: (row) => row.createdBy ?? '',
            cell: (row) => <span>{row.createdBy || '-'}</span>,
            minWidth: '110px',
        },
        {
            name: 'پارت',
            id: 'part',
            selector: (row) => row.partNumber ?? 0,
            cell: (row) => (
                <span>
                    {row.partNumber
                        ? `${row.partNumber.toLocaleString('fa-IR')} از ${(row.totalParts ?? row.partNumber).toLocaleString('fa-IR')}`
                        : '-'}
                </span>
            ),
            width: '80px',
        },
        {
            name: 'متن پیام',
            id: 'message',
            selector: (row) => row.message,
            cell: (row) => (
                <Tooltip title={row.message}>
                    <span className="sms-message-cell">{row.message}</span>
                </Tooltip>
            ),
            minWidth: '160px',
            grow: 2,
        },
        {
            name: 'کل',
            id: 'totalCount',
            selector: (row) => row.totalCount,
            cell: (row) => <span>{row.totalCount.toLocaleString('fa-IR')}</span>,
            width: '60px',
        },
        {
            name: 'ارسال‌شده · رسیده · ناموفق',
            id: 'counts',
            selector: (row) => row.failedCount,
            cell: (row) => (
                <div className="sms-counts">
                    <Tooltip title="ارسال‌شده (در انتظار تحویل)">
                        <span>
                            <Badge text={row.sentCount.toLocaleString('fa-IR')} type="waiting" />
                        </span>
                    </Tooltip>
                    <Tooltip title="رسیده">
                        <span>
                            <Badge text={row.deliveredCount.toLocaleString('fa-IR')} type="success" />
                        </span>
                    </Tooltip>
                    <Tooltip title="ناموفق">
                        <span>
                            <Badge
                                text={row.failedCount.toLocaleString('fa-IR')}
                                type={row.failedCount ? 'error' : 'info'}
                            />
                        </span>
                    </Tooltip>
                </div>
            ),
            width: '190px',
        },
        {
            name: 'آخرین استعلام',
            id: 'statusCheckedOn',
            selector: (row) => row.statusCheckedOn ?? '',
            cell: (row) => <span>{formatDate(row.statusCheckedOn)}</span>,
            minWidth: '130px',
        },
        {
            name: 'عملیات',
            id: 'action',
            width: '110px',
            cell: (row) =>
                busyPartId === row.id ? (
                    <LoadingOutlined />
                ) : (
                    <div className="actions">
                        <Tooltip title="جزئیات گیرنده‌ها">
                            <EyeOutlined onClick={() => openDetail(row)} />
                        </Tooltip>
                        <Tooltip title="استعلام وضعیت تحویل">
                            <SyncOutlined onClick={() => !busyPartId && onRowRefresh(row)} />
                        </Tooltip>
                        {row.failedCount > 0 && (
                            <Tooltip title="ارسال مجدد ناموفق‌ها">
                                <RedoOutlined onClick={() => !busyPartId && onRowRetry(row)} />
                            </Tooltip>
                        )}
                    </div>
                ),
        },
    ];

    const itemColumns: TableColumn<BulkSmsPartItem>[] = [
        {
            name: 'گیرنده',
            id: 'receiver',
            selector: (row) => row.receiver,
            cell: (row) => (
                <span className="en sms-receiver-cell">
                    {row.phoneNumber && row.phoneNumber !== row.receiver ? (
                        <>
                            {row.phoneNumber}
                            <small>{row.receiver}</small>
                        </>
                    ) : (
                        row.receiver
                    )}
                </span>
            ),
            minWidth: '160px',
        },
        {
            name: 'وضعیت',
            id: 'status',
            selector: (row) => row.status,
            cell: (row) => {
                const badge = statusBadges[row.status] ?? statusBadges[BulkSmsItemStatus.Failed];
                return <Badge text={badge.text} type={badge.type} />;
            },
            width: '120px',
        },
        {
            name: 'توضیح',
            id: 'errorMessage',
            selector: (row) => row.errorMessage ?? row.providerStatusText ?? '',
            cell: (row) => <span>{row.errorMessage || row.providerStatusText || '-'}</span>,
            minWidth: '200px',
            grow: 2,
        },
        {
            name: 'تلاش',
            id: 'attemptCount',
            selector: (row) => row.attemptCount,
            cell: (row) => <span>{row.attemptCount.toLocaleString('fa-IR')}</span>,
            width: '70px',
        },
        {
            name: 'آخرین تلاش',
            id: 'lastAttemptOn',
            selector: (row) => row.lastAttemptOn ?? '',
            cell: (row) => <span>{formatDate(row.lastAttemptOn)}</span>,
            minWidth: '150px',
        },
        {
            name: '',
            id: 'action',
            width: '60px',
            cell: (row) =>
                row.canRetry && (
                    <div className="actions">
                        <Tooltip title="ارسال مجدد">
                            <RedoOutlined
                                onClick={async () =>
                                    !actionLoading &&
                                    detailPartId &&
                                    afterDetailAction(await retry(detailPartId, setActionLoading, [row.itemId]))
                                }
                            />
                        </Tooltip>
                    </div>
                ),
        },
    ];

    const hasRetryable = detail?.items.some((item) => item.canRetry);

    return (
        <div className="sms-history">
            <Modal isOpen={detailOpen} setIsOpen={setDetailOpen} className="sms-detail-modal">
                <h4>
                    جزئیات ارسال
                    {detail?.partNumber
                        ? ` — پارت ${detail.partNumber.toLocaleString('fa-IR')} از ${(detail.totalParts ?? detail.partNumber).toLocaleString('fa-IR')}`
                        : ''}
                </h4>
                <Spin spinning={detailLoading || actionLoading}>
                    {detail && (
                        <>
                            <div className="sms-preview">{detail.message}</div>
                            <div className="sms-detail-meta">
                                <span>
                                    خط ارسال: <strong className="en">{detail.senderLine}</strong>
                                </span>
                                <span>
                                    آخرین استعلام: <strong>{formatDate(detail.statusCheckedOn)}</strong>
                                </span>
                                <label className="sms-switch">
                                    <Switch
                                        size="small"
                                        checked={onlyFailed}
                                        onChange={(checked) => {
                                            setOnlyFailed(checked);
                                            loadDetail(detail.partId, checked);
                                        }}
                                    />
                                    فقط ناموفق‌ها
                                </label>
                            </div>
                            <div className="sms-detail-table">
                                <Table
                                    className="sms-items-table"
                                    data={detail.items}
                                    column={itemColumns}
                                    setPageSize={() => undefined}
                                />
                                {!detail.items.length && <p className="sms-empty">موردی برای نمایش وجود ندارد.</p>}
                            </div>
                        </>
                    )}
                    <div className="buttons">
                        <Button
                            type={buttonType.secondary}
                            text="استعلام وضعیت تحویل"
                            icon={<SyncOutlined />}
                            disabled={!detail || actionLoading}
                            onClick={async () =>
                                detailPartId && afterDetailAction(await refreshStatus(detailPartId, setActionLoading))
                            }
                        />
                        <Button
                            type={buttonType.info}
                            text="ارسال مجدد ناموفق‌ها"
                            icon={<RedoOutlined />}
                            disabled={!hasRetryable || actionLoading}
                            onClick={async () =>
                                detailPartId && afterDetailAction(await retry(detailPartId, setActionLoading))
                            }
                        />
                        <Button type={buttonType.outlined} text="بستن" onClick={() => setDetailOpen(false)} />
                    </div>
                </Spin>
            </Modal>

            <div className="sms-history__filters">
                <Input
                    className="keyword"
                    value={filters.keyword}
                    placeholder="شناسه ارسال، شماره گیرنده یا متن پیام"
                    onPressEnter={() => load(filters)}
                    onChange={(e) => setFilters({ ...filters, keyword: e.target.value })}
                />
                <DatePicker
                    value={filters.from}
                    calendar={persian}
                    locale={persian_fa}
                    render={
                        <div>
                            <Input value={filters.from} icon={<CalendarIcon />} placeholder="از تاریخ" readOnly />
                        </div>
                    }
                    onChange={(date: any) => setFilters({ ...filters, from: date ? date.format() : '' })}
                />
                <DatePicker
                    value={filters.to}
                    calendar={persian}
                    locale={persian_fa}
                    render={
                        <div>
                            <Input value={filters.to} icon={<CalendarIcon />} placeholder="تا تاریخ" readOnly />
                        </div>
                    }
                    onChange={(date: any) => setFilters({ ...filters, to: date ? date.format() : '' })}
                />
                <label className="sms-switch">
                    <Switch
                        checked={filters.onlyWithFailed}
                        onChange={(checked) => setFilters({ ...filters, onlyWithFailed: checked })}
                    />
                    فقط دارای ناموفق
                </label>
                <Button
                    type={buttonType.success}
                    text="اعمال فیلتر"
                    loading={loading}
                    disabled={loading}
                    onClick={() => load(filters)}
                />
                <Button
                    type={buttonType.error}
                    text="حذف فیلتر"
                    disabled={loading}
                    onClick={() => {
                        setFilters(emptyFilters);
                        load(emptyFilters);
                    }}
                />
            </div>

            <Spin spinning={loading}>
                {parts?.items?.length ? (
                    <Table
                        className="sms-parts-table"
                        blur={loading}
                        data={parts.items}
                        column={columns}
                        pagination={parts.pagination}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        setloader={setLoading}
                        setdata={setParts}
                        getdata={getBulkSmsParts}
                        filters={query}
                    />
                ) : (
                    <p className="sms-empty">{loading ? 'در حال بارگذاری...' : 'سابقه‌ای یافت نشد.'}</p>
                )}
            </Spin>
        </div>
    );
};
